import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, CreditCard, TrendingUp, Percent, DollarSign,
  Award, ShoppingCart, Activity, Download, Search, 
  Filter, Store, Link2, Code, ChevronDown, ChevronUp,
  Loader2, AlertCircle, RefreshCw, ToggleLeft, ToggleRight,
  Ban, CheckCircle, Info
} from 'lucide-react';
import { customersAPI, inventoryAPI } from '../../api';
import toast from 'react-hot-toast';

// ==================== Components ====================

// KPI Card Component
const KPICard = ({ icon: Icon, title, value, subtitle, className = '', highlight = false }) => (
  <div className={`bg-white rounded-xl border ${highlight ? 'border-blue-200 ring-1 ring-blue-100' : 'border-slate-200'} p-4 ${className}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${highlight ? 'bg-blue-100' : 'bg-slate-100'} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${highlight ? 'text-blue-600' : 'text-slate-600'}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-slate-500 truncate">{title}</p>
        <p className="text-xl font-bold text-slate-900">{value ?? '-'}</p>
        {subtitle && <p className="text-xs text-slate-400 truncate">{subtitle}</p>}
      </div>
    </div>
  </div>
);

// ROI Metric Row
const ROIMetricRow = ({ label, value, prefix = '', suffix = '', tooltip }) => {
  const [showTip, setShowTip] = useState(false);
  if (value === null || value === undefined) return null;
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-sm text-slate-600 truncate">{label}</span>
        {tooltip && (
          <div className="relative flex-shrink-0">
            <button
              onMouseEnter={() => setShowTip(true)}
              onMouseLeave={() => setShowTip(false)}
              onClick={() => setShowTip((v) => !v)}
              className="text-slate-400 hover:text-slate-600 focus:outline-none"
              aria-label={`Info: ${label}`}
            >
              <Info className="w-3.5 h-3.5" />
            </button>
            {showTip && (
              <div className="absolute left-0 bottom-full mb-1.5 z-20 w-56 bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-xl leading-relaxed pointer-events-none">
                {tooltip}
                <div className="absolute top-full left-3 border-4 border-transparent border-t-slate-800" />
              </div>
            )}
          </div>
        )}
      </div>
      <span className="text-sm font-semibold text-slate-900 flex-shrink-0 ml-2">
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </span>
    </div>
  );
};

// Top Customer Row
const TopCustomerRow = ({ rank, customer, valueLabel, value }) => {
  const displayName = customer.customer_name || customer.customer_phone || customer.customer_id;
  const displayPhone = customer.customer_phone && customer.customer_name 
    ? customer.customer_phone.replace(/(\d{2})(\d+)(\d{2})/, '$1****$3')
    : null;

  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
        rank <= 3 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
      }`}>
        {rank}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{displayName}</p>
        {displayPhone && <p className="text-xs text-slate-400">{displayPhone}</p>}
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold text-slate-900">{value}</p>
        <p className="text-xs text-slate-400">{valueLabel}</p>
      </div>
    </div>
  );
};

// Source Badge
const SourceBadge = ({ source }) => {
  const config = {
    'in-store': { label: 'In-Store', color: 'bg-emerald-100 text-emerald-700', icon: Store },
    'public-tryon': { label: 'Public TryOn', color: 'bg-blue-100 text-blue-700', icon: Link2 },
    'api': { label: 'API', color: 'bg-purple-100 text-purple-700', icon: Code }
  };
  const cfg = config[source] || config['api'];
  const Icon = cfg.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

// ==================== Main Component ====================

const Customers = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [topCustomers, setTopCustomers] = useState(null);
  const [channelMix, setChannelMix] = useState(null);
  const [customerList, setCustomerList] = useState([]);
  const [listMeta, setListMeta] = useState({ total: 0, page: 1, totalPages: 0 });
  
  const [insightsTab, setInsightsTab] = useState('top'); // 'top' | 'channel'
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Fetch overview and top customers
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, topRes, channelRes] = await Promise.all([
        customersAPI.getOverview(days),
        customersAPI.getTop(days, 10),
        customersAPI.getChannelMix(days)
      ]);
      setOverview(overviewRes.data);
      setTopCustomers(topRes.data);
      setChannelMix(channelRes.data);
    } catch (error) {
      console.error('Failed to fetch customer data:', error);
      toast.error('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  }, [days]);

  // Fetch customer list
  const fetchCustomerList = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await customersAPI.getList({
        days,
        page,
        limit: 20,
        search: search || undefined,
        source: sourceFilter || undefined
      });
      setCustomerList(response.data.customers || []);
      setListMeta({
        total: response.data.total || 0,
        page: response.data.page || 1,
        totalPages: response.data.total_pages || 0
      });
    } catch (error) {
      console.error('Failed to fetch customer list:', error);
    } finally {
      setListLoading(false);
    }
  }, [days, page, search, sourceFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchCustomerList();
  }, [fetchCustomerList]);

  // Handle search with debounce
  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  // Handle export
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await customersAPI.exportCustomers(days);
      const data = response.data.data || [];
      
      // Convert to CSV
      const headers = ['Customer ID', 'Name', 'Phone', 'Sources', 'Credits Spent', 'Purchase Intent Amount', 'TryOns', 'Moments', 'Status'];
      const rows = data.map(c => [
        c.customer_id,
        c.customer_name || '',
        c.customer_phone || '',
        c.sources || '',
        c.credits_spent,
        c.purchase_intent_amount,
        c.tryon_count,
        c.moments_count,
        c.status
      ]);
      
      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers_${days}d_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Customer data exported!');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle customer block/unblock
  const handleToggleBlock = async (customer) => {
    const phone = customer.customer_phone || customer.customer_id;
    if (!phone) {
      toast.error('Cannot block: No phone number');
      return;
    }

    try {
      if (customer.status === 'blocked') {
        await inventoryAPI.unblockCustomer(phone);
        toast.success('Customer unblocked');
      } else {
        await inventoryAPI.blockCustomer(phone);
        toast.success('Customer blocked');
      }
      fetchCustomerList();
    } catch (error) {
      toast.error('Failed to update customer status');
    }
  };

  // Format helpers
  const formatCredits = (num) => {
    if (num === null || num === undefined) return '-';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num.toLocaleString();
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return '-';
    if (num >= 100000) return '₹' + (num / 100000).toFixed(1) + 'L';
    if (num >= 1000) return '₹' + (num / 1000).toFixed(1) + 'k';
    return '₹' + num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="customers-page">
      
      {/* ==================== SECTION A: HERO ==================== */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Customers</h1>
                <p className="text-slate-500 text-sm mt-1">
                  Understand customer value, credits spent, and conversion impact.
                </p>
              </div>
            </div>
            
            {/* Period Selector */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {[7, 30, 90].map(d => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    days === d
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="p-5 sm:p-6 space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPICard
              icon={Users}
              title="Total Customers"
              value={overview?.kpis?.total_customers}
              highlight
            />
            <KPICard
              icon={CreditCard}
              title="Credits Spent"
              value={formatCredits(overview?.kpis?.credits_spent)}
              subtitle="Total usage"
            />
            <KPICard
              icon={TrendingUp}
              title="Avg Credits/Customer"
              value={overview?.kpis?.avg_credits_per_customer?.toFixed(1)}
            />
            <KPICard
              icon={Percent}
              title="Conversion Rate"
              value={overview?.kpis?.conversion_rate !== null ? `${overview?.kpis?.conversion_rate}%` : null}
              subtitle="With purchase intent"
            />
          </div>

          {/* ROI Insight Box */}
          <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-slate-800">ROI Snapshot</h3>
            </div>
            <div className="space-y-0">
              <ROIMetricRow 
                label="Avg Purchase Value (Intent)" 
                value={overview?.roi_snapshot?.avg_purchase_value} 
                prefix="₹"
                tooltip="Average basket value per customer who added items to cart. Calculated as total intent amount ÷ unique customers with intent."
              />
              <ROIMetricRow 
                label="Revenue per Credit" 
                value={overview?.roi_snapshot?.revenue_per_credit} 
                prefix="₹"
                tooltip="Expected revenue generated per ₹1 of credits spent on try-ons. Total intent amount ÷ total credits spent."
              />
              <ROIMetricRow 
                label="Cost per Conversion" 
                value={overview?.roi_snapshot?.cost_per_conversion}
                suffix=" credits"
                tooltip="Credits spent per customer who showed purchase intent (added to cart). Total credits ÷ customers with intent."
              />
            </div>
            {(!overview?.roi_snapshot?.avg_purchase_value && 
              !overview?.roi_snapshot?.revenue_per_credit && 
              !overview?.roi_snapshot?.cost_per_conversion) && (
              <p className="text-sm text-slate-500 italic">
                ROI metrics will appear once purchase intents are recorded.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ==================== SECTION B: CUSTOMER INSIGHTS ==================== */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Insights Header */}
        <div className="p-5 sm:p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Activity className="w-5 h-5 text-slate-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Customer Insights</h2>
            </div>
            <button
              onClick={fetchData}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Insights Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setInsightsTab('top')}
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              insightsTab === 'top'
                ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="insights-tab-top"
          >
            Top Customers
          </button>
          <button
            onClick={() => setInsightsTab('channel')}
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              insightsTab === 'channel'
                ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="insights-tab-channel"
          >
            Channel Mix
          </button>
        </div>

        {/* Insights Content */}
        <div className="p-5 sm:p-6">
          
          {/* Top Customers Tab */}
          {insightsTab === 'top' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Top by Credits Spent */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  By Credits Spent
                </h4>
                <div className="space-y-0">
                  {(topCustomers?.top_by_credits_spent || []).slice(0, 10).map((c, i) => (
                    <TopCustomerRow
                      key={c.customer_id}
                      rank={i + 1}
                      customer={c}
                      valueLabel="credits"
                      value={formatCredits(c.value)}
                    />
                  ))}
                  {(!topCustomers?.top_by_credits_spent?.length) && (
                    <p className="text-sm text-slate-400 py-4 text-center">No data yet</p>
                  )}
                </div>
              </div>

              {/* Top by Purchase Intent Amount */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  By Purchase Intent
                </h4>
                <div className="space-y-0">
                  {(topCustomers?.top_by_purchase_intent_amount || []).slice(0, 10).map((c, i) => (
                    <TopCustomerRow
                      key={c.customer_id}
                      rank={i + 1}
                      customer={c}
                      valueLabel="intent"
                      value={formatCurrency(c.value)}
                    />
                  ))}
                  {(!topCustomers?.top_by_purchase_intent_amount?.length) && (
                    <p className="text-sm text-slate-400 py-4 text-center">No data yet</p>
                  )}
                </div>
              </div>

              {/* Top by Conversion Actions */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-purple-600" />
                  By Conversions
                </h4>
                <div className="space-y-0">
                  {(topCustomers?.top_by_conversion_actions || []).slice(0, 10).map((c, i) => (
                    <TopCustomerRow
                      key={c.customer_id}
                      rank={i + 1}
                      customer={c}
                      valueLabel="actions"
                      value={c.value}
                    />
                  ))}
                  {(!topCustomers?.top_by_conversion_actions?.length) && (
                    <p className="text-sm text-slate-400 py-4 text-center">No data yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Channel Mix Tab */}
          {insightsTab === 'channel' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customers by Source */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-4">Customers by Source</h4>
                <div className="space-y-3">
                  {[
                    { key: 'in-store', label: 'In-Store', icon: Store, color: 'emerald' },
                    { key: 'public-tryon', label: 'Public TryOn', icon: Link2, color: 'blue' },
                    { key: 'api', label: 'API', icon: Code, color: 'purple' }
                  ].map(({ key, label, icon: Icon, color }) => (
                    <div key={key} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg bg-${color}-100 flex items-center justify-center`}>
                          <Icon className={`w-4 h-4 text-${color}-600`} />
                        </div>
                        <span className="text-sm text-slate-700">{label}</span>
                      </div>
                      <span className="text-lg font-bold text-slate-900">
                        {channelMix?.customers_by_source?.[key] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Credits by Source */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-4">Credits Spent by Source</h4>
                <div className="space-y-3">
                  {[
                    { key: 'in-store', label: 'In-Store', icon: Store, color: 'emerald' },
                    { key: 'public-tryon', label: 'Public TryOn', icon: Link2, color: 'blue' },
                    { key: 'api', label: 'API', icon: Code, color: 'purple' }
                  ].map(({ key, label, icon: Icon, color }) => (
                    <div key={key} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg bg-${color}-100 flex items-center justify-center`}>
                          <Icon className={`w-4 h-4 text-${color}-600`} />
                        </div>
                        <span className="text-sm text-slate-700">{label}</span>
                      </div>
                      <span className="text-lg font-bold text-slate-900">
                        {formatCredits(channelMix?.credits_by_source?.[key] || 0)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Purchase Intent by Source (if available) */}
              {channelMix?.purchase_intent_by_source &&
                Object.values(channelMix.purchase_intent_by_source).some(v => v > 0) && (
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 md:col-span-2">
                  <h4 className="text-sm font-semibold text-emerald-800 mb-4">Purchase Intent by Source</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {Object.entries(channelMix.purchase_intent_by_source)
                      .filter(([, v]) => v > 0)
                      .map(([key, value]) => {
                        const labelMap = {
                          'in-store': 'In-Store',
                          'public-tryon': 'Public TryOn',
                          'api': 'API',
                          'sdk': 'SDK',
                          'shopify': 'Shopify',
                          'woocommerce': 'WooCommerce',
                        };
                        const label = labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/-/g, ' ');
                        return (
                          <div key={key} className="text-center">
                            <p className="text-2xl font-bold text-emerald-700">
                              {formatCurrency(value || 0)}
                            </p>
                            <p className="text-xs text-emerald-600">{label}</p>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ==================== SECTION C: CUSTOMER LIST ==================== */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* List Header */}
        <div className="p-5 sm:p-6 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-900">All Customers</h2>
            
            <div className="flex flex-wrap items-center gap-2">
              {/* Search */}
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search name or phone..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full sm:w-56 pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="customer-search"
                />
              </div>
              
              {/* Source Filter */}
              <select
                value={sourceFilter}
                onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                data-testid="source-filter"
              >
                <option value="">All Sources</option>
                <option value="in-store">In-Store</option>
                <option value="public-tryon">Public TryOn</option>
                <option value="api">API</option>
              </select>
              
              {/* Export */}
              <button
                onClick={handleExport}
                disabled={exportLoading}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                data-testid="export-btn"
              >
                {exportLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Customer Table */}
        <div className="overflow-x-auto">
          {listLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : customerList.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No customers found</p>
              <p className="text-sm text-slate-400 mt-1">Customers will appear here after they use your try-on services</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Source</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Credits Spent</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Purchase Intent</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Try-Ons</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {customerList.map((customer) => (
                  <tr key={customer.customer_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {customer.customer_name || 'Anonymous'}
                        </p>
                        {customer.customer_phone && (
                          <p className="text-xs text-slate-400">
                            {customer.customer_phone.replace(/(\d{2})(\d+)(\d{2})/, '$1****$3')}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(customer.sources || []).map(source => (
                          <SourceBadge key={source} source={source} />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-semibold text-slate-700">
                        {formatCredits(customer.credits_spent)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-semibold ${customer.purchase_intent_amount > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {customer.purchase_intent_amount > 0 ? formatCurrency(customer.purchase_intent_amount) : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-slate-600">
                        {customer.tryon_count + customer.moments_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        customer.status === 'blocked'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {customer.status === 'blocked' ? (
                          <><Ban className="w-3 h-3" /> Blocked</>
                        ) : (
                          <><CheckCircle className="w-3 h-3" /> Active</>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleToggleBlock(customer)}
                        className={`p-2 rounded-lg transition-colors ${
                          customer.status === 'blocked'
                            ? 'text-emerald-600 hover:bg-emerald-50'
                            : 'text-red-600 hover:bg-red-50'
                        }`}
                        title={customer.status === 'blocked' ? 'Unblock Customer' : 'Block Customer'}
                        data-testid={`toggle-block-${customer.customer_id}`}
                      >
                        {customer.status === 'blocked' ? (
                          <ToggleRight className="w-5 h-5" />
                        ) : (
                          <ToggleLeft className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {listMeta.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              Showing {((page - 1) * 20) + 1} - {Math.min(page * 20, listMeta.total)} of {listMeta.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-slate-600">
                Page {page} of {listMeta.totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(listMeta.totalPages, p + 1))}
                disabled={page === listMeta.totalPages}
                className="px-3 py-1 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;
